/**
 * LOCAL TERMINAL STORE  (v2 — Channel-based, output outside React)
 *
 * Architecture changes from v1:
 *
 * 1. PTY output is streamed via Tauri **Channel API** instead of events.
 *    Channels provide ordered delivery and are designed for high-throughput
 *    streaming — `app.emit()` was not.
 *
 * 2. Output is kept in a module-level **registry** (not in Zustand).  The
 *    Zustand store only holds lightweight metadata (tab id, session id,
 *    status).  This means React never re-renders for output data.
 *
 * 3. The component subscribes to the registry for direct xterm writes and
 *    replays buffered output on remount.
 *
 * 4. Backpressure: the component tracks xterm write-queue depth and calls
 *    `pty_flow(pause/resume)` when watermarks are crossed.
 */

import { create } from "zustand";
import { isTauri } from "@/hooks/usePlatform";
import { loadSecretEnvForCwd } from "@/lib/secrets/runtime-env";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LocalTerminalStatus = "connecting" | "connected" | "disconnected" | "error";
export type TerminalActivityStatus = "active" | "idle" | "disconnected" | "error";

export interface LocalTerminalTab {
  id: string;
  sessionId: string | null;
  name: string;
  status: LocalTerminalStatus;
  activityStatus: TerminalActivityStatus;
  lastOutputAt: number | null;
  error?: string;
  pendingCommand?: string;
  autoConnectOnMount: boolean;
}

interface LocalTerminalScope {
  tabs: LocalTerminalTab[];
  activeTabId: string | null;
}

interface ConnectTabOptions {
  cwd: string;
  cols: number;
  rows: number;
  command?: string;
}

interface LocalTerminalState {
  scopes: Record<string, LocalTerminalScope>;

  ensureScope: (scopeKey: string) => void;
  createTab: (
    scopeKey: string,
    name?: string,
    pendingCommand?: string,
    autoConnectOnMount?: boolean
  ) => string;
  setActiveTab: (scopeKey: string, tabId: string) => void;
  closeTab: (scopeKey: string, tabId: string, killSession?: boolean) => Promise<void>;
  connectTab: (scopeKey: string, tabId: string, opts: ConnectTabOptions) => Promise<void>;
  reconnectTab: (scopeKey: string, tabId: string, opts: ConnectTabOptions) => Promise<void>;
  writeInput: (scopeKey: string, tabId: string, data: string) => Promise<void>;
  resizeTab: (scopeKey: string, tabId: string, cols: number, rows: number) => Promise<void>;
  startIdleDetection: () => void;
  stopIdleDetection: () => void;
}

// ---------------------------------------------------------------------------
// Output registry  (module-level, NOT in Zustand / React)
// ---------------------------------------------------------------------------

/** Matches the tagged enum sent from Rust via the Channel. */
type PtyEvent =
  | { kind: "output"; data: string }
  | { kind: "exit"; code: number }
  | { kind: "activity"; busy: boolean };

interface OutputEntry {
  listeners: Set<(data: string) => void>;
  /** Chunks kept for replay on component remount. */
  chunks: string[];
  totalSize: number;
}

const outputRegistry = new Map<string, OutputEntry>();

/** Max replay buffer per session (5 MB). */
const MAX_REPLAY_BUFFER = 5_000_000;

/** Threshold (ms) after which a connected tab with no output is considered idle. */
export const TERMINAL_IDLE_THRESHOLD_MS = 60_000;

/**
 * Per-session timestamp of last Zustand `set()` for `lastOutputAt`.
 * Used to throttle store updates to max 1x per second per tab — the actual
 * output still streams to xterm at full speed via the registry.
 */
const lastOutputSetAt = new Map<string, number>();

/**
 * Sessions that have received an authoritative foreground busy/idle signal from
 * the Rust poller. For these, the 60s output-based idle sweep stops deriving
 * active/idle (the poller owns it); the sweep stays a fallback for sessions where
 * the foreground pid is indeterminate.
 */
const pollerDrivenSessions = new Set<string>();

function getOrCreateEntry(sessionId: string): OutputEntry {
  let entry = outputRegistry.get(sessionId);
  if (!entry) {
    entry = { listeners: new Set(), chunks: [], totalSize: 0 };
    outputRegistry.set(sessionId, entry);
  }
  return entry;
}

/**
 * Subscribe to live output for a session.
 *
 * Returns an unsubscribe function.  Called by LocalTerminalView to wire
 * output directly to `terminal.write()` without going through React.
 */
export function subscribeToOutput(
  sessionId: string,
  callback: (data: string) => void
): () => void {
  const entry = getOrCreateEntry(sessionId);
  entry.listeners.add(callback);
  return () => {
    entry.listeners.delete(callback);
  };
}

/** Return accumulated replay chunks for a session. */
export function getReplayBuffer(sessionId: string): string[] {
  return outputRegistry.get(sessionId)?.chunks ?? [];
}

/** Clean up the output entry when a tab/session is explicitly discarded. */
function clearOutputEntry(sessionId: string): void {
  outputRegistry.delete(sessionId);
  lastOutputSetAt.delete(sessionId);
  pollerDrivenSessions.delete(sessionId);
}

/**
 * Detect OSC 133 semantic-prompt markers in a raw output chunk to derive whether
 * the shell is running a command or waiting at a prompt. These are explicit escape
 * sequences emitted only by shells with integration configured, so there are no
 * false positives — when absent this returns null and idle-detection governs.
 *
 *   133;A prompt start · 133;B input start · 133;C command output · 133;D command done
 *
 * A/B/D mean the shell is idle (a command finished or a fresh prompt is waiting);
 * C means a command is actively running. The last marker in the chunk wins.
 */
export function detectPromptActivity(data: string): "active" | "idle" | null {
  if (!data.includes("\x1b]133;")) return null;
  const re = /\x1b\]133;([ABCD])/g;
  let last: string | null = null;
  let m: RegExpExecArray | null;
  while ((m = re.exec(data)) !== null) last = m[1];
  if (last === null) return null;
  return last === "C" ? "active" : "idle";
}

/** Internal: called by the Channel callback on each output message. */
function pushOutput(sessionId: string, data: string): void {
  const entry = getOrCreateEntry(sessionId);

  // Append to replay buffer.
  entry.chunks.push(data);
  entry.totalSize += data.length;

  // Trim oldest chunks to stay within budget.
  while (entry.totalSize > MAX_REPLAY_BUFFER && entry.chunks.length > 1) {
    const removed = entry.chunks.shift()!;
    entry.totalSize -= removed.length;
  }

  // Fan out to live listeners (direct xterm writes).
  entry.listeners.forEach((cb) => cb(data));

  // A command-finished / prompt marker is an authoritative "done" signal — flush
  // it immediately (bypassing the throttle) so "My Agents" reflects it at once.
  const promptActivity = detectPromptActivity(data);
  const now = Date.now();
  if (promptActivity === "idle") {
    lastOutputSetAt.set(sessionId, now);
    updateLastOutputAt(sessionId, now, "idle");
    return;
  }

  // Throttled update of lastOutputAt in Zustand (max 1x/sec per session).
  const lastSet = lastOutputSetAt.get(sessionId) ?? 0;
  if (now - lastSet >= 1000) {
    lastOutputSetAt.set(sessionId, now);
    updateLastOutputAt(sessionId, now);
  }
}

/**
 * Update `lastOutputAt` and `activityStatus` for the tab that owns
 * `sessionId`.  Deferred to avoid import-order issues with the store.
 */
function updateLastOutputAt(
  sessionId: string,
  timestamp: number,
  activityStatus: TerminalActivityStatus = "active",
): void {
  const store = useLocalTerminalStore;
  const state = store.getState();
  for (const [scopeKey, scope] of Object.entries(state.scopes)) {
    const idx = scope.tabs.findIndex((t) => t.sessionId === sessionId);
    if (idx === -1) continue;
    store.setState((prev) => {
      const s = prev.scopes[scopeKey];
      if (!s) return prev;
      return {
        scopes: {
          ...prev.scopes,
          [scopeKey]: {
            ...s,
            tabs: s.tabs.map((tab) =>
              tab.sessionId === sessionId
                ? { ...tab, lastOutputAt: timestamp, activityStatus }
                : tab
            ),
          },
        },
      };
    });
    break;
  }
}

/** Interval handle for the idle-detection sweep. */
let idleDetectionInterval: ReturnType<typeof setInterval> | null = null;

// ---------------------------------------------------------------------------
// Tauri helpers
// ---------------------------------------------------------------------------

let tabIdCounter = 0;
const generateTabId = () => `lterm-${Date.now()}-${++tabIdCounter}`;

async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

/**
 * State updater that marks a tab as errored (its PTY session is gone). Reused by
 * the connect/write/resize failure paths so a dead session surfaces to the user
 * instead of silently dropping input.
 */
function markTabErrored(
  scopeKey: string,
  tabId: string,
  message: string
): (state: LocalTerminalState) => Partial<LocalTerminalState> {
  return (state) => {
    const scope = state.scopes[scopeKey];
    if (!scope) return state;
    return {
      scopes: {
        ...state.scopes,
        [scopeKey]: {
          ...scope,
          tabs: scope.tabs.map((tab) =>
            tab.id === tabId
              ? {
                  ...tab,
                  sessionId: null,
                  status: "error" as const,
                  activityStatus: "error" as const,
                  error: message,
                  autoConnectOnMount: false,
                }
              : tab
          ),
        },
      },
    };
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useLocalTerminalStore = create<LocalTerminalState>((set, get) => ({
  scopes: {},

  ensureScope: (scopeKey: string) => {
    set((state) => {
      if (state.scopes[scopeKey]) return state;
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: { tabs: [], activeTabId: null },
        },
      };
    });
  },

  createTab: (
    scopeKey: string,
    name?: string,
    pendingCommand?: string,
    autoConnectOnMount = true
  ): string => {
    const tabId = generateTabId();

    set((state) => {
      const scope = state.scopes[scopeKey] ?? { tabs: [], activeTabId: null };
      const tabNumber = scope.tabs.length + 1;

      const newTab: LocalTerminalTab = {
        id: tabId,
        sessionId: null,
        name: name || `Terminal ${tabNumber}`,
        status: "disconnected",
        activityStatus: "disconnected",
        lastOutputAt: null,
        pendingCommand,
        autoConnectOnMount,
      };

      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: {
            tabs: [...scope.tabs, newTab],
            activeTabId: tabId,
          },
        },
      };
    });

    return tabId;
  },

  setActiveTab: (scopeKey: string, tabId: string) => {
    set((state) => {
      const scope = state.scopes[scopeKey];
      if (!scope) return state;
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: { ...scope, activeTabId: tabId },
        },
      };
    });
  },

  closeTab: async (scopeKey: string, tabId: string, killSession = true) => {
    const tab = get().scopes[scopeKey]?.tabs.find((t) => t.id === tabId);
    if (!tab) return;

    if (killSession && tab.sessionId && isTauri()) {
      clearOutputEntry(tab.sessionId);
      await tauriInvoke("pty_kill", { sessionId: tab.sessionId }).catch(() => {});
    }

    set((state) => {
      const scope = state.scopes[scopeKey];
      if (!scope) return state;

      const tabs = scope.tabs.filter((t) => t.id !== tabId);
      const nextActive =
        scope.activeTabId === tabId
          ? tabs.length > 0
            ? tabs[tabs.length - 1].id
            : null
          : scope.activeTabId;

      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: { tabs, activeTabId: nextActive },
        },
      };
    });
  },

  connectTab: async (scopeKey: string, tabId: string, opts: ConnectTabOptions) => {
    if (!isTauri()) {
      set((state) => {
        const scope = state.scopes[scopeKey];
        if (!scope) return state;
        return {
          scopes: {
            ...state.scopes,
            [scopeKey]: {
              ...scope,
              tabs: scope.tabs.map((tab) =>
                tab.id === tabId
                  ? { ...tab, status: "error" as const, activityStatus: "error" as const, error: "Only available in desktop app" }
                  : tab
              ),
            },
          },
        };
      });
      return;
    }

    const current = get().scopes[scopeKey]?.tabs.find((t) => t.id === tabId);
    if (!current) return;
    if (current.status === "connecting") return;

    // Already connected — just sync size.
    if (current.sessionId && current.status === "connected") {
      if (opts.cols >= 2 && opts.rows >= 2) {
        try {
          await tauriInvoke("pty_resize", {
            sessionId: current.sessionId,
            cols: opts.cols,
            rows: opts.rows,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          set(markTabErrored(scopeKey, tabId, message));
        }
      }
      return;
    }

    const safeCols = opts.cols >= 2 ? opts.cols : 80;
    const safeRows = opts.rows >= 2 ? opts.rows : 24;

    // Mark connecting.
    set((state) => {
      const scope = state.scopes[scopeKey];
      if (!scope) return state;
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: {
            ...scope,
            tabs: scope.tabs.map((tab) =>
              tab.id === tabId ? { ...tab, status: "connecting" as const, error: undefined } : tab
            ),
          },
        },
      };
    });

    let sessionId: string | null = null;

    try {
      // Create the Tauri Channel — this is the high-throughput pipe from
      // the Rust reader thread directly into JS, replacing the old
      // `app.emit()` event system.
      const { Channel } = await import("@tauri-apps/api/core");
      const channel = new Channel<PtyEvent>();

      channel.onmessage = (event: PtyEvent) => {
        if (event.kind === "output") {
          if (!sessionId) return;
          pushOutput(sessionId, event.data);
        } else if (event.kind === "activity") {
          // Authoritative foreground busy/idle from the Rust poller. Once a session
          // reports activity, the 60s output-based idle sweep defers to it (see the
          // sweep in startIdleDetection) so an idle shell isn't re-marked active.
          if (sessionId) pollerDrivenSessions.add(sessionId);
          const nextStatus: TerminalActivityStatus = event.busy ? "active" : "idle";
          set((state) => {
            const scope = state.scopes[scopeKey];
            if (!scope) return state;
            let changed = false;
            const tabs = scope.tabs.map((tab) => {
              if (tab.id !== tabId || tab.status !== "connected") return tab;
              if (tab.activityStatus === nextStatus) return tab;
              changed = true;
              return { ...tab, activityStatus: nextStatus };
            });
            if (!changed) return state;
            return { scopes: { ...state.scopes, [scopeKey]: { ...scope, tabs } } };
          });
        } else if (event.kind === "exit") {
          set((state) => {
            const scope = state.scopes[scopeKey];
            if (!scope) return state;
            return {
              scopes: {
                ...state.scopes,
                [scopeKey]: {
                  ...scope,
                  tabs: scope.tabs.map((tab) =>
                    tab.id === tabId
                      // Keep sessionId so replay buffer remains addressable after process exit.
                      ? { ...tab, status: "disconnected" as const, activityStatus: "disconnected" as const }
                      : tab
                  ),
                },
              },
            };
          });
        }
      };

      const envVars = await loadSecretEnvForCwd(opts.cwd).catch(() => ({}));

      sessionId = await tauriInvoke<string>("pty_create", {
        projectId: `${scopeKey}:${tabId}`,
        cwd: opts.cwd,
        cols: safeCols,
        rows: safeRows,
        envVars,
        env_vars: envVars,
        onEvent: channel,
      });

      const launchCommand = opts.command ?? current.pendingCommand;

      // Mark connected.
      set((state) => {
        const scope = state.scopes[scopeKey];
        if (!scope) return state;
        return {
          scopes: {
            ...state.scopes,
            [scopeKey]: {
              ...scope,
              tabs: scope.tabs.map((tab) =>
                tab.id === tabId
                  ? {
                      ...tab,
                      sessionId,
                      status: "connected" as const,
                      activityStatus: "active" as const,
                      lastOutputAt: Date.now(),
                      error: undefined,
                      pendingCommand: undefined,
                      autoConnectOnMount: false,
                    }
                  : tab
              ),
            },
          },
        };
      });

      if (launchCommand) {
        await tauriInvoke("pty_write", {
          sessionId,
          data: `${launchCommand}\n`,
        });
      }
    } catch (err) {
      if (sessionId) {
        clearOutputEntry(sessionId);
        await tauriInvoke("pty_kill", { sessionId }).catch(() => {});
      }

      const message = err instanceof Error ? err.message : String(err);
      set(markTabErrored(scopeKey, tabId, message));
    }
  },

  reconnectTab: async (scopeKey: string, tabId: string, opts: ConnectTabOptions) => {
    const tab = get().scopes[scopeKey]?.tabs.find((t) => t.id === tabId);
    if (!tab) return;

    if (tab.sessionId && isTauri()) {
      clearOutputEntry(tab.sessionId);
      await tauriInvoke("pty_kill", { sessionId: tab.sessionId }).catch(() => {});
    }

    set((state) => {
      const scope = state.scopes[scopeKey];
      if (!scope) return state;
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: {
            ...scope,
            tabs: scope.tabs.map((t) =>
              t.id === tabId
                ? { ...t, sessionId: null, status: "disconnected" as const, activityStatus: "disconnected" as const, error: undefined, autoConnectOnMount: false }
                : t
            ),
          },
        },
      };
    });

    await get().connectTab(scopeKey, tabId, opts);
  },

  writeInput: async (scopeKey: string, tabId: string, data: string) => {
    if (!isTauri()) return;
    const tab = get().scopes[scopeKey]?.tabs.find((t) => t.id === tabId);
    if (!tab?.sessionId || tab.status !== "connected") return;
    try {
      await tauriInvoke("pty_write", { sessionId: tab.sessionId, data });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      set(markTabErrored(scopeKey, tabId, message));
    }
  },

  resizeTab: async (scopeKey: string, tabId: string, cols: number, rows: number) => {
    if (!isTauri()) return;
    if (cols < 2 || rows < 2) return;
    const tab = get().scopes[scopeKey]?.tabs.find((t) => t.id === tabId);
    if (!tab?.sessionId || tab.status !== "connected") return;
    try {
      await tauriInvoke("pty_resize", { sessionId: tab.sessionId, cols, rows });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      set(markTabErrored(scopeKey, tabId, message));
    }
  },

  startIdleDetection: () => {
    if (idleDetectionInterval) return; // already running
    idleDetectionInterval = setInterval(() => {
      const state = get();
      const now = Date.now();
      let anyChanged = false;

      const nextScopes: Record<string, LocalTerminalScope> = {};
      for (const [key, scope] of Object.entries(state.scopes)) {
        let scopeChanged = false;
        const nextTabs = scope.tabs.map((tab) => {
          let next: TerminalActivityStatus;

          const pollerOwned =
            tab.status === "connected" &&
            tab.sessionId !== null &&
            pollerDrivenSessions.has(tab.sessionId);

          if (tab.status === "disconnected") {
            next = "disconnected";
          } else if (tab.status === "error") {
            next = "error";
          } else if (pollerOwned) {
            // The Rust foreground poller owns active/idle for this session; the
            // output-based heuristic below would wrongly flip an idle shell back
            // to active whenever it recently drew its prompt.
            next = tab.activityStatus;
          } else if (
            tab.status === "connected" &&
            tab.lastOutputAt !== null &&
            now - tab.lastOutputAt > TERMINAL_IDLE_THRESHOLD_MS
          ) {
            next = "idle";
          } else if (tab.status === "connected" || tab.status === "connecting") {
            next = "active";
          } else {
            next = tab.activityStatus;
          }

          if (next !== tab.activityStatus) {
            scopeChanged = true;
            anyChanged = true;
            return { ...tab, activityStatus: next };
          }
          return tab;
        });
        nextScopes[key] = scopeChanged ? { ...scope, tabs: nextTabs } : scope;
      }

      if (anyChanged) {
        set({ scopes: nextScopes });
      }
    }, 5000);
  },

  stopIdleDetection: () => {
    if (idleDetectionInterval) {
      clearInterval(idleDetectionInterval);
      idleDetectionInterval = null;
    }
  },
}));
